import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { post } from '@/shared/api/api_client';
import Modal from '@/shared/ui/Modal/Modal';
import Button from '@/shared/ui/Button/Button';
import Input from '@/shared/ui/Input/Input';
import { cn } from '@/shared/utils/cn';
import type { FamilyMember } from '@/entities/user';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface AddFamilyMemberProps {
  familyId: string;
  onSuccess: (member: FamilyMember) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Step 1 schema — profile
// ---------------------------------------------------------------------------
const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'Max 50 characters'),
  lastName: z.string().max(50, 'Max 50 characters').optional(),
  middleName: z.string().max(50, 'Max 50 characters').optional(),
  dateOfBirth: z.string().optional(),
  sex: z
    .enum(['male', 'female', 'other', 'prefer_not_to_say', ''])
    .optional(),
  role: z.enum(['parent', 'child'], {
    required_error: 'Role is required',
  }),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

// ---------------------------------------------------------------------------
// Step 2 schema — login credentials (child only)
// ---------------------------------------------------------------------------
const loginSchema = z
  .object({
    enableLogin: z.boolean(),
    pin: z.string().optional(),
    confirmPin: z.string().optional(),
    canChangePin: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.enableLogin) return;

    if (!data.pin || !/^\d{4,6}$/.test(data.pin)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'PIN must be 4–6 digits',
        path: ['pin'],
      });
    }

    if (data.pin !== data.confirmPin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'PINs do not match',
        path: ['confirmPin'],
      });
    }
  });

type LoginFormValues = z.infer<typeof loginSchema>;

// ---------------------------------------------------------------------------
// API response type
// ---------------------------------------------------------------------------
interface CreateMemberResponse {
  member: FamilyMember;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AddFamilyMember({
  familyId,
  onSuccess,
  onClose,
}: AddFamilyMemberProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<1 | 2>(1);
  const [createdMember, setCreatedMember] = useState<FamilyMember | null>(null);
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  // ------------------------------------------------------------------
  // Step 1 — profile form
  // ------------------------------------------------------------------
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    watch: watchProfile,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      middleName: '',
      dateOfBirth: '',
      sex: '',
      role: 'child',
    },
  });

  const selectedRole = watchProfile('role');

  const createMemberMutation = useMutation({
    mutationFn: (data: ProfileFormValues) =>
      post<CreateMemberResponse>(`/families/${familyId}/members`, {
        firstName: data.firstName,
        lastName: data.lastName || null,
        middleName: data.middleName || null,
        dateOfBirth: data.dateOfBirth || null,
        sex: data.sex || null,
        role: data.role,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['members', familyId] });
      setCreatedMember(res.member);
      if (selectedRole === 'child') {
        setStep(2);
      } else {
        onSuccess(res.member);
      }
    },
  });

  const onProfileSubmit = (data: ProfileFormValues) => {
    createMemberMutation.mutate(data);
  };

  // ------------------------------------------------------------------
  // Step 2 — login form
  // ------------------------------------------------------------------
  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    watch: watchLogin,
    control: loginControl,
    formState: { errors: loginErrors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      enableLogin: false,
      pin: '',
      confirmPin: '',
      canChangePin: true,
    },
  });

  const enableLogin = watchLogin('enableLogin');

  const enableLoginMutation = useMutation({
    mutationFn: (data: { pin: string; canChangePin: boolean }) =>
      post<void>(`/auth/members/${createdMember!.memberId}/enable-login`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', familyId] });
      onSuccess(createdMember!);
    },
  });

  const onLoginSubmit = (data: LoginFormValues) => {
    if (!data.enableLogin) {
      onSuccess(createdMember!);
      return;
    }
    enableLoginMutation.mutate({
      pin: data.pin!,
      canChangePin: data.canChangePin,
    });
  };

  // ------------------------------------------------------------------
  // Render step 1
  // ------------------------------------------------------------------
  const renderStep1 = (): React.ReactElement => (
    <form
      onSubmit={handleProfileSubmit(onProfileSubmit)}
      noValidate
      className="flex flex-col gap-4"
    >
      {/* First name */}
      <Input
        label="First name"
        placeholder="e.g. Alex"
        error={profileErrors.firstName?.message}
        autoComplete="given-name"
        {...registerProfile('firstName')}
      />

      {/* Last name */}
      <Input
        label="Last name (optional)"
        placeholder="e.g. Smith"
        error={profileErrors.lastName?.message}
        autoComplete="family-name"
        {...registerProfile('lastName')}
      />

      {/* Middle name */}
      <Input
        label="Middle name (optional)"
        placeholder="e.g. Jordan"
        error={profileErrors.middleName?.message}
        autoComplete="additional-name"
        {...registerProfile('middleName')}
      />

      {/* Date of birth */}
      <Input
        label="Date of birth (optional)"
        type="date"
        error={profileErrors.dateOfBirth?.message}
        {...registerProfile('dateOfBirth')}
      />

      {/* Sex */}
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700">
          Sex (optional)
        </label>
        <select
          className={cn(
            'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'transition-colors duration-150',
          )}
          {...registerProfile('sex')}
        >
          <option value="">— Select —</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
          <option value="prefer_not_to_say">Prefer not to say</option>
        </select>
        {profileErrors.sex && (
          <p role="alert" className="text-xs text-danger-600">
            {profileErrors.sex.message}
          </p>
        )}
      </div>

      {/* Role */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-700">Role</span>
        <div className="flex gap-4">
          {(['parent', 'child'] as const).map((r) => (
            <label key={r} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value={r}
                className="h-4 w-4 text-primary-600 border-slate-300 focus:ring-primary-500"
                {...registerProfile('role')}
              />
              <span className="text-sm text-slate-700 capitalize">{r}</span>
            </label>
          ))}
        </div>
        {profileErrors.role && (
          <p role="alert" className="text-xs text-danger-600">
            {profileErrors.role.message}
          </p>
        )}
      </div>

      {/* API error */}
      {createMemberMutation.isError && (
        <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {createMemberMutation.error instanceof Error
            ? createMemberMutation.error.message
            : 'Failed to add family member. Please try again.'}
        </p>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" isLoading={createMemberMutation.isPending}>
          {selectedRole === 'child' ? 'Next' : 'Add Member'}
        </Button>
      </div>
    </form>
  );

  // ------------------------------------------------------------------
  // Render step 2
  // ------------------------------------------------------------------
  const renderStep2 = (): React.ReactElement => (
    <form
      onSubmit={handleLoginSubmit(onLoginSubmit)}
      noValidate
      className="flex flex-col gap-4"
    >
      <p className="text-sm text-slate-600">
        Profile created for <strong>{createdMember?.displayName}</strong>. Would you like to enable login for this child?
      </p>

      {/* Enable login toggle */}
      <Controller
        control={loginControl}
        name="enableLogin"
        render={({ field }) => (
          <label className="flex items-center gap-3 cursor-pointer rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={field.value}
              onChange={field.onChange}
              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-slate-700">
              Allow this child to log in?
            </span>
          </label>
        )}
      />

      {/* Credentials — only shown when toggle is on */}
      {enableLogin && (
        <div className="flex flex-col gap-4 rounded-lg border border-primary-100 bg-primary-50/40 p-4">
          <p className="text-xs text-slate-500">
            They'll log in using the family code + their first name + this PIN.
          </p>

          {/* PIN */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700">
              PIN
            </label>
            <div className="relative">
              <input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                autoComplete="new-password"
                aria-invalid={loginErrors.pin ? 'true' : undefined}
                className={cn(
                  'block w-full rounded-lg border bg-white px-3 py-2 pr-10 text-xl tracking-[0.4em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal',
                  'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150',
                  loginErrors.pin
                    ? 'border-danger-400 focus:ring-danger-400'
                    : 'border-slate-300 focus:ring-primary-500 focus:border-primary-500',
                )}
                {...registerLogin('pin')}
              />
              <button
                type="button"
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                onClick={() => setShowPin((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
              >
                {showPin ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              </button>
            </div>
            <p className="text-xs text-slate-500">4–6 digits</p>
            {loginErrors.pin && (
              <p role="alert" className="text-xs text-danger-600">
                {loginErrors.pin.message}
              </p>
            )}
          </div>

          {/* Confirm PIN */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700">
              Confirm PIN
            </label>
            <div className="relative">
              <input
                type={showConfirmPin ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                autoComplete="new-password"
                aria-invalid={loginErrors.confirmPin ? 'true' : undefined}
                className={cn(
                  'block w-full rounded-lg border bg-white px-3 py-2 pr-10 text-xl tracking-[0.4em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal',
                  'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150',
                  loginErrors.confirmPin
                    ? 'border-danger-400 focus:ring-danger-400'
                    : 'border-slate-300 focus:ring-primary-500 focus:border-primary-500',
                )}
                {...registerLogin('confirmPin')}
              />
              <button
                type="button"
                aria-label={showConfirmPin ? 'Hide confirm PIN' : 'Show confirm PIN'}
                onClick={() => setShowConfirmPin((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPin ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              </button>
            </div>
            {loginErrors.confirmPin && (
              <p role="alert" className="text-xs text-danger-600">
                {loginErrors.confirmPin.message}
              </p>
            )}
          </div>

          {/* Can change PIN */}
          <Controller
            control={loginControl}
            name="canChangePin"
            render={({ field }) => (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={field.onChange}
                  className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700">
                  Allow child to change their own PIN
                </span>
              </label>
            )}
          />
        </div>
      )}

      {/* API error */}
      {enableLoginMutation.isError && (
        <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {enableLoginMutation.error instanceof Error
            ? enableLoginMutation.error.message
            : 'Failed to enable login. Please try again.'}
        </p>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onSuccess(createdMember!)}
        >
          Skip
        </Button>
        <Button
          type="submit"
          isLoading={enableLoginMutation.isPending}
        >
          {enableLogin ? 'Enable Login & Finish' : 'Finish'}
        </Button>
      </div>
    </form>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={step === 1 ? 'Add Family Member' : 'Set Up Login'}
      size="md"
    >
      {/* Step indicator */}
      <div className="mb-5 flex items-center gap-2">
        {[1, 2].map((s) => (
          <React.Fragment key={s}>
            <div
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                s === step
                  ? 'bg-primary-600 text-white'
                  : s < step
                  ? 'bg-secondary-500 text-white'
                  : 'bg-slate-200 text-slate-500',
              )}
            >
              {s}
            </div>
            {s < 2 && (
              <div
                className={cn(
                  'h-0.5 flex-1',
                  step > s ? 'bg-secondary-400' : 'bg-slate-200',
                )}
              />
            )}
          </React.Fragment>
        ))}
        <span className="ml-2 text-xs text-slate-500">
          {step === 1 ? 'Profile' : 'Login'}
        </span>
      </div>

      {step === 1 ? renderStep1() : renderStep2()}
    </Modal>
  );
}
